/**
 * Zustand store для аутентификации: хранение токена, пользователя, permissions
 * Использует localStorage для персистентности между сессиями
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface User {
  id: string;
  username: string;
  full_name: string;
  email?: string;
  tenant_id?: string;
  role_id: string;
  role_name: string;
}

interface AuthState {
  token: string | null;
  user: User | null;
  permissions: string[];
  isAuthenticated: boolean;
  // Дата: 15.01.2026 - Для SuperAdmin: выбранный tenant
  selectedTenantId: string | null;
  setAuth: (token: string, user: User, permissions: string[]) => void;
  logout: () => void;
  // Дата: 15.01.2026 - Установить выбранный tenant (для SuperAdmin)
  setSelectedTenant: (tenantId: string | null) => void;
  // Дата: 15.01.2026 - Получить эффективный tenant_id (для API запросов)
  getEffectiveTenantId: () => string | undefined;
}

// Helper для работы с cookie (для nginx auth_request)
const setCookie = (name: string, value: string, days: number) => {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${value}; path=/; expires=${expires}; SameSite=Strict`;
};

const deleteCookie = (name: string) => {
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      permissions: [],
      isAuthenticated: false,
      selectedTenantId: null,
      setAuth: (token, user, permissions) => {
        // Также устанавливаем cookie для nginx auth_request (Jaeger)
        setCookie('auth_token', token, 1); // 1 день
        set({ token, user, permissions, isAuthenticated: true });
      },
      logout: () => {
        // Удаляем cookie
        deleteCookie('auth_token');
        set({ token: null, user: null, permissions: [], isAuthenticated: false, selectedTenantId: null });
      },
      // Дата: 15.01.2026 - Установить выбранный tenant (для SuperAdmin)
      setSelectedTenant: (tenantId) => set({ selectedTenantId: tenantId }),
      // Дата: 15.01.2026 - Получить эффективный tenant_id:
      // - Для обычных пользователей: user.tenant_id
      // - Для SuperAdmin: selectedTenantId
      getEffectiveTenantId: () => {
        const { user, selectedTenantId } = get();
        // Если у пользователя есть tenant_id — он не SuperAdmin
        if (user?.tenant_id) return user.tenant_id;
        // SuperAdmin — используем выбранный tenant
        return selectedTenantId || undefined;
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

